import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { ACCOUNT_NUMBER_MAX_LENGTH, ACCOUNT_NUMBER_REGEX } from '@constants/account-number.constant';
import { environment } from '@env';
import { AccountNumberFormControl } from '@forms/account-number-form-control';
import { SubPcgEntryForm } from '@forms/sub-pcg-entry-form';
import { FormHelper } from '@helpers/form.helper';
import { PcgEntryHelper } from '@helpers/pcg-entry.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { PcgEntry } from '@models/pcg-entry';
import { SubPcgEntry } from '@models/sub-pcg-entry';

export type PcgEntryFormType = {
  id: FormControl<number | undefined>;
  accountNumber: AccountNumberFormControl;
  label: FormControl<string | null>;
  centralizer: FormControl<boolean | undefined>;
  centralizerPcgEntry: SubPcgEntryForm;
};

export class PcgEntryForm extends FormGroup<PcgEntryFormType> {
  static readonly centralizerAccountShouldBeNumericError = 'centralizerAccountShouldBeNumeric';
  static readonly centralizerPcgEntryRequiredError = 'centralizerPcgEntryRequired';

  get accountNumber(): AccountNumberFormControl {
    return this.controls.accountNumber;
  }

  get accountNumberErrorMessage(): ErrorMessage | null {
    const errorMessage: ErrorMessage | null = FormHelper.composeErrorMessages(this.accountNumber, [
      {
        method: 'getEmptyErrorMessage',
        args: ['Le numéro de compte est requis', 'Champ vide']
      },
      {
        method: 'getMaxLengthErrorMessage',
        args: [
          `Le format n'est pas correct, un numéro de compte en alphanumérique ne peut comporter que ${ACCOUNT_NUMBER_MAX_LENGTH} caractères maximum.`,
          'Erreur de format'
        ]
      }
    ]);

    if (errorMessage) {
      return errorMessage;
    }

    const centralizerShoubBeNumericErrorMessage: ErrorMessage | null = FormHelper.simpleErrorMessage(this, {
      simpleErrorCode: PcgEntryForm.centralizerAccountShouldBeNumericError,
      simpleErrorMessage: `Le numéro de compte d'un compte centralisateur doit être numérique`
    });

    if (centralizerShoubBeNumericErrorMessage) {
      return centralizerShoubBeNumericErrorMessage;
    }

    if (this.accountNumber.touched) {
      if (this.accountNumber.hasError('pattern')) {
        if (this.additionalPattern && !this.additionalPattern.test(this.accountNumber.value)) {
          if (!environment.production && !this.additionalPatternErrorMessage) {
            console.warn(`Message d'erreur manquant pour le cas d'une regex supplémentaire`);
          }
          return {
            error: 'Erreur de format',
            message: this.additionalPatternErrorMessage || `Le format n'est pas valide.`
          };
        }
        if (this.accountNumber.value.match(/[a-z]/i)) {
          return {
            error: 'Erreur de format',
            message: `Le format n'est pas correct, si votre numéro de compte est en alphanumérique, il ne doit pas commencer par 445, 5, 6, 7.`
          };
        }
        return {
          error: 'Erreur de format',
          message: `Un numéro de compte doit commencer par un chiffre compris entre 1 et 7 (inclus), suivi de 1 à 7 chiffres.`
        };
      }
    }

    return null;
  }

  get centralizer(): FormControl<boolean | undefined> {
    return this.controls.centralizer;
  }

  get centralizerPcgEntry(): SubPcgEntryForm {
    return this.controls.centralizerPcgEntry;
  }

  get centralizerPcgEntryErrorMessage(): ErrorMessage | null {
    return FormHelper.simpleErrorMessage(this, {
      simpleErrorCode: PcgEntryForm.centralizerPcgEntryRequiredError,
      simpleErrorMessage: 'Le compte de centralisation est requis.',
      simpleError: 'Champ requis'
    });
  }

  get label(): FormControl<string | null> {
    return this.controls.label;
  }

  get labelErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.label, 'Le nom de compte ne peut être vide.');
  }

  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  constructor(
    private additionalPattern?: RegExp,
    private additionalPatternErrorMessage?: string
  ) {
    super({
      id: new FormControl<number | undefined>(undefined),
      accountNumber: new AccountNumberFormControl(null, [
        Validators.required,
        Validators.maxLength(ACCOUNT_NUMBER_MAX_LENGTH),
        Validators.pattern(ACCOUNT_NUMBER_REGEX),
        control => {
          if (!additionalPattern) {
            return null;
          }
          return Validators.pattern(additionalPattern)(control);
        }
      ]),
      label: new FormControl<string | null>(null, Validators.required),
      centralizer: new FormControl<boolean | undefined>(undefined),
      centralizerPcgEntry: new SubPcgEntryForm()
    });
    this.addValidators([PcgEntryForm.centralizerAccountShouldBeNumeric(), PcgEntryForm.centralizerPcgEntryRequired()]);
  }

  static centralizerAccountShouldBeNumeric(): ValidatorFn {
    return (form: PcgEntryForm) => {
      if (!form.accountNumber.value) {
        return null;
      }

      if (PcgEntryForm.notStartingWith4AndContainsALetter(form.accountNumber.value) && form.centralizer.value) {
        return {
          [PcgEntryForm.centralizerAccountShouldBeNumericError]: true
        };
      }

      return null;
    };
  }

  static centralizerPcgEntryRequired(): ValidatorFn {
    return (form: PcgEntryForm) => {
      if (!form.accountNumber.value) {
        return null;
      }

      if (
        PcgEntryForm.notStartingWith4AndContainsALetter(form.accountNumber.value) &&
        !form.centralizer.value &&
        !form.centralizerPcgEntry.id.value
      ) {
        return {
          [PcgEntryForm.centralizerPcgEntryRequiredError]: true
        };
      }

      return null;
    };
  }

  private static notStartingWith4AndContainsALetter(accountNumber: string): boolean {
    return PcgEntryHelper.isAuxiliaryAccountNumber(accountNumber);
  }

  fromPcgEntry(pcgEntry: PcgEntry): void {
    this.patchValue({
      id: pcgEntry.id,
      accountNumber: pcgEntry.accountNumber,
      label: pcgEntry.label,
      centralizer: pcgEntry.centralizer
    });

    if (pcgEntry.centralizerPcgEntry) {
      this.centralizerPcgEntry.fromSubPcgEntry(pcgEntry.centralizerPcgEntry);
    }
  }

  resetCentralizerPcgEntryForm(): void {
    this.centralizerPcgEntry.fromSubPcgEntry(new SubPcgEntry());
  }

  toPcgEntry(): PcgEntry {
    return this.getRawValue() as PcgEntry;
  }
}
